import { WSList } from "@wingspanhq/fe-component-library";
import { useRef, useState } from "react";
import { get, useFormContext } from "react-hook-form";
import { queriesPayeeRows } from "@query";
import { useAccountAccess } from "../../../../v3/hooks/useAccountAccess";
import { useCreateContractorModal } from "../CreateContractorModal/useCreateContractorModal";
import { SelectEngagementForPayee } from "../SelectEngagementForPayee";
import { SelectPayee } from "../SelectPayee";

type Props = {
  /** Form path holding `{ payeeId, payeeEngagementId }`. */
  name: string;
  disabled?: boolean;
  preselectedEmail?: string;
};

/**
 * The contractor half of a payable / invoice split: the contractor and the
 * assignment are picked separately, because v3 addresses them separately
 * (`payeeId` + `payeeEngagementId` are both required fields on `Payable` and
 * `InvoiceSplit`). Only the contractor list is searched; the assignment options
 * come from that contractor's own engagement collection.
 */
export const FormFieldsPayeeAndEngagement: React.FC<Props> = props => {
  const { setValue, errors, clearErrors, watch, register } = useFormContext();
  const modalCreateContractor = useCreateContractorModal();
  const { hasScope } = useAccountAccess();

  const namePayeeId = `${props.name}.payeeId`;
  const namePayeeEngagementId = `${props.name}.payeeEngagementId`;

  const payeeId = watch(namePayeeId);
  const payeeEngagementId = watch(namePayeeEngagementId);

  const [autoFocus, setAutoFocus] = useState(false);
  // The preselection is applied once. `onSuccess` also fires on refetches, and
  // re-applying it later would reset the contractor while leaving the assignment
  // the user picked for it — a mismatched pair.
  const isPreselectionAppliedRef = useRef(false);

  const canAddContractor = hasScope("payments.payee");

  // Selecting the contractor drops the assignment: an assignment only exists in
  // the context of the contractor it belongs to.
  const selectPayee = (newPayeeId: string | null) => {
    clearErrors(props.name);
    setValue(namePayeeId, newPayeeId);
    setValue(namePayeeEngagementId, null);
  };

  // A preselected email resolves to a contractor through the search rows (the
  // only email-addressable read); its assignment then auto-selects when there is
  // just one. Anything less certain focuses the picker instead of guessing.
  queriesPayeeRows.searchPayeeRows.usePaginatedQuery(
    {
      filter: {
        searchString: props.preselectedEmail
          ? { eq: props.preselectedEmail }
          : undefined
      }
    },
    {
      enabled: !!props.preselectedEmail,
      onSuccess: result => {
        if (!props.preselectedEmail || isPreselectionAppliedRef.current) {
          return;
        }
        isPreselectionAppliedRef.current = true;

        const payee = result.pages
          .flatMap(page => page.data ?? [])
          .find(payee => payee.email === props.preselectedEmail);

        if (payee) {
          setAutoFocus(false);
          setValue(namePayeeId, payee.id);
        } else {
          setAutoFocus(true);
        }
      }
    }
  );

  return (
    <>
      <input type="hidden" name={namePayeeId} ref={register()} />
      <input type="hidden" name={namePayeeEngagementId} ref={register()} />

      <WSList gap="M">
        <SelectPayee
          label="Contractor"
          placeholder="Select a contractor"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          defaultSearchText={props.preselectedEmail}
          disabled={props.disabled}
          value={payeeId ?? null}
          onChange={selectPayee}
          status={get(errors, namePayeeId) ? "error" : undefined}
          message={get(errors, namePayeeId)?.message}
          menuFooterText={
            canAddContractor
              ? "Don't see a Contractor name? Add a new contractor to your contractor list."
              : undefined
          }
          menuFooterAction={
            canAddContractor
              ? {
                  label: "Add contractor",
                  onClick: async () => {
                    const payee = await modalCreateContractor.open();

                    if (payee) {
                      selectPayee(payee.id ?? null);
                    }
                  }
                }
              : undefined
          }
        />

        <SelectEngagementForPayee
          label="Assignment"
          payeeId={payeeId}
          disabled={props.disabled || !payeeId}
          value={payeeEngagementId ?? null}
          onChange={newValue => {
            clearErrors(props.name);
            setValue(namePayeeEngagementId, newValue);
          }}
          autoSelectSingleOption
          status={get(errors, namePayeeEngagementId) ? "error" : undefined}
          message={get(errors, namePayeeEngagementId)?.message}
        />
      </WSList>
    </>
  );
};
